import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

const DISCOUNT_OPTIONS = [
  { id: "var_pct", label: "Discount Percentage", display: "Var %", type: "percentage", variable: true },
  { id: "var_amt", label: "Discount Amount", display: "Var", type: "amount", variable: true },
  { id: "staff", label: "Staff Discount", display: "20 %", type: "percentage", value: 20 },
  { id: "d10", label: "Discount", display: "10 %", type: "percentage", value: 10 },
  { id: "d20", label: "Discount", display: "20 %", type: "percentage", value: 20 },
  { id: "d50", label: "Discount", display: "50 %", type: "percentage", value: 50 },
  { id: "d75", label: "Discount", display: "75 %", type: "percentage", value: 75 },
  { id: "d100", label: "Discount", display: "100 %", type: "percentage", value: 100 },
];

const srCellSx = {
  width: 32,
  minWidth: 32,
  maxWidth: 32,
  px: 0.5,
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "visible",
};

const inputSx = {
  width: 100,
  flex: "1 1 0",
  minWidth: 0,
  "& .MuiInputBase-root": { height: 30, fontSize: "0.8rem", borderRadius: 1 },
  "& .MuiInputBase-input": { py: 0.5, px: 1 },
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px !important",
    borderColor: "#000 !important",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px !important",
    borderColor: "#000 !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px !important",
    borderColor: "#000 !important",
  },
};

const RhpLineDiscountDialog = ({
  open,
  onClose,
  onApply,
  listPrice = 0,
}) => {
  const [selectedOptionId, setSelectedOptionId] = useState("var_amt");
  const [percentageInput, setPercentageInput] = useState("0.00");
  const [amountInput, setAmountInput] = useState("0.00");

  const maxAmount = listPrice > 0 ? listPrice : 0;

  const selectedOption = useMemo(
    () => DISCOUNT_OPTIONS.find((option) => option.id === selectedOptionId) ?? DISCOUNT_OPTIONS[1],
    [selectedOptionId]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedOptionId("var_amt");
    setPercentageInput("0.00");
    setAmountInput("0.00");
  }, [open]);

  const syncAmountFromPercentage = (pct) => {
    if (!Number.isFinite(pct) || listPrice <= 0) {
      return;
    }
    const amount = Math.min(maxAmount, (listPrice * pct) / 100);
    setAmountInput(amount.toFixed(2));
  };

  const syncPercentageFromAmount = (amount) => {
    if (!Number.isFinite(amount) || listPrice <= 0) {
      return;
    }
    const pct = Math.min(100, (amount / listPrice) * 100);
    setPercentageInput(pct.toFixed(2));
  };

  const handleSelectOption = (option) => {
    setSelectedOptionId(option.id);
    if (option.variable) {
      if (option.type === "percentage") {
        setPercentageInput("0.00");
        setAmountInput("0.00");
      } else {
        setPercentageInput("0.00");
        setAmountInput("0.00");
      }
      return;
    }
    if (option.type === "percentage") {
      const pct = Math.min(100, Number(option.value));
      setPercentageInput(pct.toFixed(2));
      syncAmountFromPercentage(pct);
      return;
    }
    const amt = Math.min(maxAmount, Number(option.value));
    setAmountInput(amt.toFixed(2));
    syncPercentageFromAmount(amt);
  };

  const handlePercentageChange = (value) => {
    if (value === "" || value === ".") {
      setPercentageInput(value);
      return;
    }
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    const capped = Math.min(100, Math.max(0, parsed));
    const nextValue = value.endsWith(".") && capped === parsed ? value : String(capped);
    setPercentageInput(nextValue);
    if (Number.isFinite(capped)) {
      syncAmountFromPercentage(capped);
    }
  };

  const handlePercentageBlur = () => {
    const parsed = parseFloat(percentageInput);
    if (!Number.isFinite(parsed)) {
      setPercentageInput("0.00");
      setAmountInput("0.00");
      return;
    }
    const capped = Math.min(100, Math.max(0, parsed));
    setPercentageInput(capped.toFixed(2));
    syncAmountFromPercentage(capped);
  };

  const handleAmountChange = (value) => {
    if (value === "" || value === ".") {
      setAmountInput(value);
      return;
    }
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    const cap = maxAmount > 0 ? maxAmount : parsed;
    const capped = Math.min(cap, Math.max(0, parsed));
    const nextValue = value.endsWith(".") && capped === parsed ? value : String(capped);
    setAmountInput(nextValue);
    if (Number.isFinite(capped) && listPrice > 0) {
      syncPercentageFromAmount(capped);
    }
  };

  const handleAmountBlur = () => {
    const parsed = parseFloat(amountInput);
    if (!Number.isFinite(parsed)) {
      setAmountInput("0.00");
      setPercentageInput("0.00");
      return;
    }
    const cap = maxAmount > 0 ? maxAmount : parsed;
    const capped = Math.min(cap, Math.max(0, parsed));
    setAmountInput(capped.toFixed(2));
    syncPercentageFromAmount(capped);
  };

  const handleSubmit = () => {
    if (selectedOption.variable) {
      if (selectedOption.type === "percentage") {
        const parsed = parseFloat(percentageInput);
        if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
          return;
        }
        onApply({ discountType: "percentage", discountValue: Math.min(100, parsed).toFixed(2) });
        return;
      }
      const parsed = parseFloat(amountInput);
      if (!Number.isFinite(parsed) || parsed < 0) {
        return;
      }
      const capped = maxAmount > 0 ? Math.min(maxAmount, parsed) : parsed;
      onApply({ discountType: "amount", discountValue: capped.toFixed(2) });
      return;
    }

    if (selectedOption.type === "percentage") {
      onApply({
        discountType: "percentage",
        discountValue: Math.min(100, Number(selectedOption.value)).toFixed(2),
      });
      return;
    }

    onApply({
      discountType: "amount",
      discountValue: (maxAmount > 0
        ? Math.min(maxAmount, Number(selectedOption.value))
        : Number(selectedOption.value)
      ).toFixed(2),
    });
  };

  const percentageDisabled = selectedOption.type === "amount" && !selectedOption.variable;
  const amountDisabled = selectedOption.type === "percentage" && !selectedOption.variable;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: 360, maxWidth: "95vw" } }}
    >
      <DialogTitle sx={{ py: 1, px: 2, fontSize: "0.95rem" }}>Discount</DialogTitle>
      <DialogContent sx={{ pt: 0, px: 2, pb: 2 }}>
        <TableContainer sx={{ maxHeight: 200 }}>
          <Table size="small" className="table-cell-border" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow className="table-cell-header">
                <TableCell sx={srCellSx}>#</TableCell>
                <TableCell sx={{ fontSize: "0.75rem", px: 1 }}>Description</TableCell>
                <TableCell align="right" sx={{ width: 64, fontSize: "0.75rem", px: 1 }}>
                  Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DISCOUNT_OPTIONS.map((option, index) => {
                const isSelected = option.id === selectedOptionId;
                return (
                  <TableRow
                    key={option.id}
                    hover
                    selected={isSelected}
                    onClick={() => handleSelectOption(option)}
                    sx={{
                      cursor: "pointer",
                      "& .MuiTableCell-root": { py: 0.5, fontSize: "0.75rem" },
                      ...(isSelected ? { bgcolor: "action.selected" } : {}),
                    }}
                  >
                    <TableCell sx={srCellSx}>{index + 1}</TableCell>
                    <TableCell sx={{ px: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {option.label}
                    </TableCell>
                    <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
                      {option.display}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            gap: 1.5,
            alignItems: "center",
            mt: 1.5,
            pt: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: "1 1 0", minWidth: 0 }}>
            <Typography variant="caption" fontWeight={600} sx={{ flexShrink: 0 }}>
              Percentage
            </Typography>
            <TextField
              size="small"
              value={percentageInput}
              onChange={(e) => handlePercentageChange(e.target.value)}
              onBlur={handlePercentageBlur}
              disabled={percentageDisabled}
              inputProps={{ inputMode: "decimal", max: 100, min: 0 }}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <Typography variant="caption" sx={{ pr: 0.5, fontSize: "0.7rem" }}>
                    %
                  </Typography>
                ),
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: "1 1 0", minWidth: 0 }}>
            <Typography variant="caption" fontWeight={600} sx={{ flexShrink: 0 }}>
              Amount
            </Typography>
            <TextField
              size="small"
              value={amountInput}
              onChange={(e) => handleAmountChange(e.target.value)}
              onBlur={handleAmountBlur}
              disabled={amountDisabled}
              inputProps={{
                inputMode: "decimal",
                min: 0,
                max: maxAmount > 0 ? maxAmount : undefined,
              }}
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <Typography variant="caption" sx={{ pl: 0.5, fontSize: "0.7rem" }}>
                    £
                  </Typography>
                ),
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Button variant="contained" size="small" onClick={handleSubmit} sx={{ minWidth: 72 }}>
            Ok
          </Button>
          <Button
            variant="outlined"
            size="small"
            className="button-variant-outlined"
            onClick={onClose}
            sx={{ minWidth: 72 }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RhpLineDiscountDialog;
